/**
 * RateFornitoriTab — Vista Contabilità: elenco rate fornitori raggruppate.
 * Quick-action + bulk payment + export Excel + link a richiesta origine.
 */
import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, FileText, AlertTriangle, Filter, Download, X } from "lucide-react";
import api from "@/api";
import StoricoRichiestaModal from "@/pages/richieste/StoricoRichiestaModal";

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

function statoRata(rata, today) {
  if (rata.data_pagamento) return "pagato";
  if (rata.stato_manuale === "annullato") return "annullato";
  const d = rata.data_scadenza || "";
  if (!d) return "da_pagare";
  if (d < today) return "scaduto";
  const inScad = new Date(today);
  inScad.setDate(inScad.getDate() + 7);
  if (d <= inScad.toISOString().slice(0, 10)) return "in_scadenza";
  return "da_pagare";
}

const STATO_STYLE = {
  da_pagare:    { label: "Da pagare",   cls: "bg-slate-100 text-slate-700 border-slate-300" },
  in_scadenza:  { label: "In scadenza", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  scaduto:      { label: "Scaduto",     cls: "bg-rose-100 text-rose-800 border-rose-300" },
  pagato:       { label: "Pagato",      cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  annullato:    { label: "Annullato",   cls: "bg-slate-100 text-slate-400 border-slate-200" },
};

export default function RateFornitoriTab() {
  const qc = useQueryClient();
  const [storicoRichiesta, setStoricoRichiesta] = React.useState(null);
  const [filtroFornitore, setFiltroFornitore] = React.useState("");
  const [filtroStato, setFiltroStato] = React.useState("");
  const [filtroCantiere, setFiltroCantiere] = React.useState("");
  const [selected, setSelected] = React.useState(new Set());
  const today = new Date().toISOString().slice(0, 10);

  const { data: costi = [], isLoading } = useQuery({
    queryKey: ["costi-variabili-rate-fornitori"],
    queryFn: () => api.get("/costi-variabili?include_bolle=false").then((r) => r.data),
  });
  const { data: subappalti = [], isError: erroreSubappalti } = useQuery({
    queryKey: ["subappalti-contabilita"],
    queryFn: () => api.get("/subappalti").then((r) => r.data),
    retry: false,
  });

  const saldiSubappalti = (Array.isArray(subappalti) ? subappalti : [])
    .filter(s => s.stato !== "annullato")
    .map(s => {
      const totale = Number(s.importo_totale) || 0;
      const pagato = (s.pagamenti || []).filter(p => p.quietanzato === true)
        .reduce((sum, p) => sum + (Number(p.importo) || 0), 0);
      return { ...s, totale, pagato, residuo: Math.max(0, totale - pagato) };
    })
    .filter(s => s.residuo > 0);

  const { data: cantieriMap = {} } = useQuery({
    queryKey: ["cantieri-map-simple"],
    queryFn: async () => {
      const r = await api.get("/cantieri?tipo=all");
      return Object.fromEntries((r.data || []).map((c) => [c.cantiere_id, c]));
    },
  });

  const rate = React.useMemo(
    () => (costi || []).filter((c) => c.origine === "richiesta_fornitore"),
    [costi]
  );

  const filtered = React.useMemo(() => {
    return rate.filter((r) => {
      if (filtroFornitore && !(r.fornitore || "").toLowerCase().includes(filtroFornitore.toLowerCase())) return false;
      if (filtroCantiere && r.cantiere_id !== filtroCantiere) return false;
      const st = statoRata(r, today);
      if (filtroStato && st !== filtroStato) return false;
      return true;
    });
  }, [rate, filtroFornitore, filtroStato, filtroCantiere, today]);

  const byFornitore = React.useMemo(() => {
    const m = {};
    for (const r of filtered) {
      const k = r.fornitore || "—";
      if (!m[k]) m[k] = { fornitore: k, rate: [], totale_imponibile: 0, totale_con_iva: 0, pagato: 0, da_pagare: 0 };
      m[k].rate.push(r);
      m[k].totale_imponibile += Number(r.importo_imponibile) || 0;
      const iva = 1 + (Number(r.aliquota_iva) || 0) / 100;
      const tot = (Number(r.importo_imponibile) || 0) * iva;
      m[k].totale_con_iva += tot;
      if (r.data_pagamento) m[k].pagato += tot;
      else m[k].da_pagare += tot;
    }
    return Object.values(m).sort((a, b) => b.da_pagare - a.da_pagare);
  }, [filtered]);

  const kpi = React.useMemo(() => {
    let daPagare = 0, pagate = 0, scadute = 0, inScad = 0;
    for (const r of filtered) {
      const iva = 1 + (Number(r.aliquota_iva) || 0) / 100;
      const tot = (Number(r.importo_imponibile) || 0) * iva;
      const st = statoRata(r, today);
      if (st === "pagato") pagate += tot;
      if (st === "da_pagare") daPagare += tot;
      if (st === "scaduto") scadute += tot;
      if (st === "in_scadenza") inScad += tot;
    }
    return { daPagare, pagate, scadute, inScad, count: filtered.length };
  }, [filtered, today]);

  const fornitoriUnique = React.useMemo(
    () => Array.from(new Set(rate.map((r) => r.fornitore).filter(Boolean))).sort(),
    [rate]
  );

  // Rate selezionabili (non pagate/annullate)
  const selectableIds = React.useMemo(
    () => filtered.filter((r) => !r.data_pagamento && r.stato_manuale !== "annullato").map((r) => r.costo_id),
    [filtered]
  );
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));

  const toggle = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(selectableIds));
  };
  const clearSel = () => setSelected(new Set());

  const selectedTotale = React.useMemo(() => {
    let t = 0;
    for (const r of filtered) {
      if (selected.has(r.costo_id)) {
        const iva = 1 + (Number(r.aliquota_iva) || 0) / 100;
        t += (Number(r.importo_imponibile) || 0) * iva;
      }
    }
    return t;
  }, [filtered, selected]);

  const bulkPaga = useMutation({
    mutationFn: () => api.post("/costi-variabili/bulk/paga", {
      costo_ids: Array.from(selected),
      data_pagamento: today,
    }).then((r) => r.data),
    onSuccess: (data) => {
      toast.success(`${data.paid}/${data.total} rate segnate come pagate`);
      clearSel();
      qc.invalidateQueries({ queryKey: ["costi-variabili-rate-fornitori"] });
      qc.invalidateQueries({ queryKey: ["richieste-materiali"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore bulk pagamento"),
  });

  const segnaPagata = useMutation({
    mutationFn: (costo) => {
      const params = new URLSearchParams({
        importo_effettivo: String(costo.importo_imponibile),
        data_pagamento: today,
      });
      return api.post(`/costi-variabili/${costo.costo_id}/paga?${params.toString()}`).then((r) => r.data);
    },
    onSuccess: () => {
      toast.success("Rata segnata come pagata");
      qc.invalidateQueries({ queryKey: ["costi-variabili-rate-fornitori"] });
      qc.invalidateQueries({ queryKey: ["richieste-materiali"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore aggiornamento"),
  });

  const apriStorico = async (r) => {
    if (!r.linked_richiesta_id) return;
    try {
      const res = await api.get(`/richieste-materiali/${r.linked_richiesta_id}`);
      setStoricoRichiesta(res.data);
    } catch {
      toast.error("Impossibile caricare la richiesta origine");
    }
  };

  const exportUrl = React.useMemo(() => {
    const params = new URLSearchParams();
    if (filtroFornitore) params.set("fornitore", filtroFornitore);
    if (filtroCantiere) params.set("cantiere_id", filtroCantiere);
    if (filtroStato) params.set("stato", filtroStato);
    const qs = params.toString();
    return `${REACT_APP_BACKEND_URL}/api/costi-variabili/rate-fornitori/export.xlsx${qs ? `?${qs}` : ""}`;
  }, [filtroFornitore, filtroCantiere, filtroStato]);

  return (
    <div className="space-y-4" data-testid="tab-rate-fornitori">
      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard label="Rate totali" value={kpi.count} mono />
        <KpiCard label="Da pagare" value={eur(kpi.daPagare)} tone="slate" />
        <KpiCard label="In scadenza (7gg)" value={eur(kpi.inScad)} tone="amber" />
        <KpiCard label="Scadute" value={eur(kpi.scadute)} tone="rose" />
        <KpiCard label="Già pagate" value={eur(kpi.pagate)} tone="emerald" />
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4">
        <h3 className="text-sm font-bold mb-2">Saldi subappalti</h3>
        {erroreSubappalti && <p className="text-red-600 text-xs">Errore caricamento subappalti</p>}
        {saldiSubappalti.map(s => (
          <div key={s.subappalto_id} className="flex justify-between border-b py-2 text-xs">
            <span>{s.ragione_sociale} — {s.stato === "bozza" ? "Bozza, da autorizzare" : s.stato}</span>
            <span>Totale: {eur(s.totale)} | Pagato: {eur(s.pagato)} | Residuo: {eur(s.residuo)}</span>
          </div>
        ))}
      </div>

      {/* Filtri + Export */}
      <div className="bg-slate-50 border border-slate-200 rounded-md p-3 flex flex-wrap items-center gap-2 text-xs">
        <Filter className="w-4 h-4 text-slate-500" />
        <select value={filtroFornitore} onChange={(e) => setFiltroFornitore(e.target.value)} data-testid="filter-fornitore-rate" className="px-2 py-1 border border-slate-300 rounded-sm bg-white">
          <option value="">Tutti fornitori</option>
          {fornitoriUnique.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <select value={filtroCantiere} onChange={(e) => setFiltroCantiere(e.target.value)} data-testid="filter-cantiere-rate" className="px-2 py-1 border border-slate-300 rounded-sm bg-white">
          <option value="">Tutti cantieri</option>
          {Object.values(cantieriMap).map((c) => <option key={c.cantiere_id} value={c.cantiere_id}>{c.nome}</option>)}
        </select>
        <select value={filtroStato} onChange={(e) => setFiltroStato(e.target.value)} data-testid="filter-stato-rate" className="px-2 py-1 border border-slate-300 rounded-sm bg-white">
          <option value="">Tutti gli stati</option>
          <option value="scaduto">Solo SCADUTE</option>
          <option value="in_scadenza">Solo IN SCADENZA (7gg)</option>
          <option value="da_pagare">Solo DA PAGARE</option>
          <option value="pagato">Solo PAGATE</option>
        </select>
        <a
          href={exportUrl}
          target="_blank"
          rel="noreferrer"
          data-testid="btn-export-xlsx"
          className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-sm"
        >
          <Download className="w-3.5 h-3.5" />
          <span>⬇ Excel</span>
        </a>
      </div>

      {/* Sticky bulk action bar */}
      {selected.size > 0 && (
        <div className="sticky top-0 z-20 bg-emerald-600 text-white p-3 rounded-md flex items-center justify-between shadow-lg" data-testid="bulk-bar">
          <div className="text-sm font-semibold">
            ✓ {selected.size} rat{selected.size === 1 ? "a" : "e"} selezionat{selected.size === 1 ? "a" : "e"} · Totale <b>{eur(selectedTotale)}</b>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => bulkPaga.mutate()}
              disabled={bulkPaga.isPending}
              data-testid="btn-bulk-paga"
              className="px-4 py-2 bg-white text-emerald-700 hover:bg-emerald-50 disabled:opacity-60 font-bold rounded-sm inline-flex items-center gap-1"
            >
              <CheckCircle2 className="w-4 h-4" />
              {bulkPaga.isPending ? "…" : `Segna pagate le ${selected.size} selezionate`}
            </button>
            <button
              onClick={clearSel}
              data-testid="btn-bulk-clear"
              className="p-2 hover:bg-emerald-700 rounded-sm"
              title="Deseleziona tutto"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Elenco per fornitore */}
      {isLoading ? (
        <div className="p-8 text-center text-slate-400">Caricamento…</div>
      ) : byFornitore.length === 0 ? (
        <div className="p-8 bg-white border border-slate-200 text-center text-slate-500">
          Nessuna rata fornitore.<br />
          <span className="text-[11px] text-slate-400">Le rate compaiono qui quando accetti una Richiesta fornitore da un cantiere.</span>
        </div>
      ) : (
        byFornitore.map((grp) => (
          <div key={grp.fornitore} className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid={`gruppo-${grp.fornitore}`}>
            <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="font-display font-bold text-slate-900">🏢 {grp.fornitore}</div>
                <div className="text-[11px] text-slate-500">{grp.rate.length} rat{grp.rate.length === 1 ? "a" : "e"}</div>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <div><span className="text-slate-500">Da pagare:</span> <b className="text-slate-900">{eur(grp.da_pagare)}</b></div>
                <div><span className="text-slate-500">Pagato:</span> <b className="text-emerald-700">{eur(grp.pagato)}</b></div>
                <div><span className="text-slate-500">Totale:</span> <b>{eur(grp.totale_con_iva)}</b></div>
              </div>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left bg-slate-50/50 text-slate-600 text-[11px] uppercase tracking-widest">
                  <th className="p-2 w-8">
                    <input
                      type="checkbox"
                      checked={allSelected && grp.rate.every((r) => r.data_pagamento || selected.has(r.costo_id) || r.stato_manuale === "annullato")}
                      onChange={toggleAll}
                      title="Seleziona/deseleziona tutte le rate pagabili"
                      data-testid="check-all-rate"
                      className="w-4 h-4"
                    />
                  </th>
                  <th className="p-2">Descrizione</th>
                  <th className="p-2">Cantiere</th>
                  <th className="p-2">Scadenza</th>
                  <th className="p-2 text-right">Imponibile</th>
                  <th className="p-2 text-right">Totale IVA</th>
                  <th className="p-2">Stato</th>
                  <th className="p-2 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {grp.rate.map((r) => {
                  const st = statoRata(r, today);
                  const style = STATO_STYLE[st] || STATO_STYLE.da_pagare;
                  const iva = 1 + (Number(r.aliquota_iva) || 0) / 100;
                  const tot = (Number(r.importo_imponibile) || 0) * iva;
                  const cant = cantieriMap[r.cantiere_id];
                  const selectable = !r.data_pagamento && r.stato_manuale !== "annullato";
                  const isSel = selected.has(r.costo_id);
                  return (
                    <tr key={r.costo_id} className={`border-t border-slate-100 hover:bg-slate-50 ${isSel ? "bg-emerald-50" : ""}`} data-testid={`rata-${r.costo_id}`}>
                      <td className="p-2">
                        {selectable && (
                          <input
                            type="checkbox"
                            checked={isSel}
                            onChange={() => toggle(r.costo_id)}
                            data-testid={`check-${r.costo_id}`}
                            className="w-4 h-4"
                          />
                        )}
                      </td>
                      <td className="p-2">
                        <div className="text-xs font-semibold">{r.descrizione}</div>
                        {r.rata_numero && (
                          <div className="text-[10px] text-slate-500 font-mono">Rata {r.rata_numero}/{r.rata_totale}</div>
                        )}
                      </td>
                      <td className="p-2 text-xs">{cant?.nome || "—"}</td>
                      <td className="p-2 font-mono text-xs">
                        {r.data_scadenza || "—"}
                        {st === "scaduto" && (
                          <div className="text-[10px] text-rose-700 font-bold">
                            <AlertTriangle className="w-3 h-3 inline" /> Scaduta
                          </div>
                        )}
                      </td>
                      <td className="p-2 text-right font-mono text-xs">{eur(r.importo_imponibile)}</td>
                      <td className="p-2 text-right font-mono text-xs font-bold">{eur(tot)}</td>
                      <td className="p-2">
                        <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] font-semibold border ${style.cls}`}>
                          {style.label}
                        </span>
                        {r.data_pagamento && (
                          <div className="text-[10px] text-emerald-700 font-mono">📅 {r.data_pagamento}</div>
                        )}
                      </td>
                      <td className="p-2 text-right whitespace-nowrap">
                        {selectable && (
                          <button
                            onClick={() => segnaPagata.mutate(r)}
                            disabled={segnaPagata.isPending}
                            data-testid={`btn-paga-${r.costo_id}`}
                            title="Segna come pagata oggi"
                            className="p-1 text-emerald-700 hover:bg-emerald-50 border border-emerald-300 rounded-sm inline-flex items-center gap-1"
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            <span className="text-[11px] font-semibold">Paga</span>
                          </button>
                        )}
                        {r.linked_richiesta_id && (
                          <button
                            onClick={() => apriStorico(r)}
                            title="Apri richiesta origine"
                            data-testid={`btn-origine-${r.costo_id}`}
                            className="ml-1 p-1 text-blue-700 hover:bg-blue-50 border border-blue-200 rounded-sm inline-flex items-center gap-1"
                          >
                            <FileText className="w-3.5 h-3.5" />
                            <span className="text-[11px] font-semibold">Origine</span>
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))
      )}

      {storicoRichiesta && (
        <StoricoRichiestaModal richiesta={storicoRichiesta} onClose={() => setStoricoRichiesta(null)} />
      )}
    </div>
  );
}

function KpiCard({ label, value, tone = "slate", mono = false }) {
  const tones = {
    slate:   "text-slate-900",
    emerald: "text-emerald-700",
    amber:   "text-amber-700",
    rose:    "text-rose-700",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3">
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`font-display font-black text-2xl ${tones[tone] || tones.slate} ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}
